import {
  useOperationDeps,
  WorkOrderOperationDependency,
} from "@/queries/operation-deps/use-operation-deps";
import { useMemo } from "react";
import { DAG, Edge, Vertex } from "@/components/ui/dag/dag";
import { QueryError } from "@/components/ui/query-error/query-error";
import { useTranslation } from "react-i18next";

export function RoutingGraph() {
  const { t } = useTranslation();
  const { operationDeps, isPending, error, refetch } = useOperationDeps();
  const { vertices, edges } = useMemo(() => {
    return isPending ? buildFakeGraph() : buildGraph(operationDeps);
  }, [operationDeps, isPending]);

  if (error) {
    return (
      <QueryError
        resource={t("routing.title")}
        errors={[error]}
        retries={[refetch]}
      />
    );
  }

  return (
    <DAG
      loading={isPending}
      layout={{
        nodesep: 0,
        ranksep: 0,
        edgesep: 0,
      }}
      defaultVertex={{
        width: 280,
        height: 80,
      }}
      vertices={vertices}
      edges={edges}
    />
  );
}

function buildGraph(data: WorkOrderOperationDependency[]) {
  const vertices = new Map<string, Vertex>();
  for (const dep of data.flatMap((dep) => [dep.FromWOO!, dep.ToWOO!])) {
    vertices.set(dep.Id, {
      id: dep.Id,
      name: dep.Sequence + "-" + dep.Name!,
      desc: dep.Description!,
      active: dep.Status.StatusNId === "Active",
    });
  }

  const edges = data.map(
    (dep): Edge => ({
      in: dep.ToWOO_Id!,
      out: dep.FromWOO_Id!,
      label: dep.Id,
    })
  );

  return {
    vertices: Array.from(vertices.values()),
    edges,
  } as const;
}

function buildFakeGraph() {
  const vertices: Vertex[] = [
    { id: "1" },
    { id: "2" },
    { id: "3" },
    { id: "4" },
    { id: "5" },
    { id: "6" },
    { id: "7" },
    { id: "8" },
  ];
  const edges: Edge[] = [
    { out: "1", in: "2" },
    { out: "1", in: "3" },
    { out: "2", in: "4" },
    { out: "4", in: "6" },
    { out: "6", in: "8" },
    { out: "3", in: "5" },
    { out: "5", in: "7" },
    { out: "7", in: "8" },
  ];
  return { vertices, edges } as const;
}
